import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/InputDefault';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import IconProvider from '@/components/ui/IconProvider';
import { getFolderPath } from '@/lib/stores/folderStore';
import { apiService } from '@/lib/api-functions/api-service';

interface CodeIdInputProps {
  value?: string;
  objectName: string;
  folderId?: string | null;
  gameId: string;
  onChange: (codeId: string) => void;
  disabled?: boolean;
  mode?: 'create' | 'edit' | 'view';
  existingObjectId?: string; // For edit mode, exclude current object from uniqueness check
}

export function CodeIdInput({
  value = '',
  objectName,
  folderId,
  gameId,
  onChange,
  disabled = false,
  mode = 'create',
  existingObjectId
}: CodeIdInputProps) {
  const [localValue, setLocalValue] = useState(value);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    message?: string;
  } | null>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounced validation
  useEffect(() => {
    if (!localValue) {
      setValidationResult(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      await validateCodeId(localValue);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [localValue, gameId, existingObjectId]);

  const validateCodeId = async (codeId: string) => {
    if (!codeId) {
      setValidationResult(null);
      return;
    }

    setIsValidating(true);
    try {
      // Call backend validation endpoint
      const token = await apiService.getToken();
      if (!token) {
        setValidationResult({ isValid: false, message: 'Authentication required' });
        return;
      }

      // For now, we'll do client-side validation
      // In production, you'd want to call the backend API
      const sanitized = sanitizeCodeId(codeId);
      
      if (sanitized !== codeId) {
        setValidationResult({ 
          isValid: false, 
          message: 'Code ID contains invalid characters. Use lowercase letters, numbers, and underscores only.' 
        });
      } else {
        setValidationResult({ 
          isValid: true, 
          message: 'Code ID is valid' 
        });
      }
    } catch (error) {
      console.error('Error validating code ID:', error);
      setValidationResult({ isValid: false, message: 'Validation error' });
    } finally {
      setIsValidating(false);
    }
  };

  const sanitizeCodeId = (codeId: string): string => {
    return codeId
      .toLowerCase()
      .replace(/[^a-z0-9/]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/\/_+|_+\//g, '/');
  };

  const generateCodeId = () => {
    if (!objectName) return;

    // Get folder path
    let path = '';
    if (folderId) {
      path = getFolderPath(gameId, 'library', folderId);
      // Remove leading slash and convert to lowercase with underscores
      path = path
        .replace(/^\//, '')
        .toLowerCase()
        .replace(/\//g, '/');
    } else {
      path = 'root';
    }

    // Sanitize object name
    const sanitizedName = objectName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');

    const generatedId = `${path}_${sanitizedName}`;
    setLocalValue(generatedId);
    onChange(generatedId);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);
  };

  const handleBlur = () => {
    // Sanitize on blur
    const sanitized = sanitizeCodeId(localValue);
    if (sanitized !== localValue) {
      setLocalValue(sanitized);
      onChange(sanitized);
    }
  };

  const isReadOnly = mode === 'view' || disabled;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label htmlFor="code_id" className="text-sm font-medium">
          Code ID
        </Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help">
                <IconProvider iconstring="fa-info-circle" size={16} />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>
                A unique identifier used to reference this object in code exports.
                Auto-generated from folder path and object name (e.g., "characters/npcs_hero").
                You can edit this to create a custom identifier.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Input
            id="code_id"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="e.g., characters/npcs_hero"
            disabled={isReadOnly}
            className={`pr-8 `}
          />
         
        </div>
        {!isReadOnly && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={generateCodeId}
            disabled={!objectName}
            className="shrink-0 flex items-center gap-1"
          >
            <IconProvider iconstring="fa-refresh" size={12} />
            Auto-generate
          </Button>
        )}
      </div>

      {validationResult && !validationResult.isValid && (
        <p className="text-sm text-red-500">{validationResult.message}</p>
      )}
      {!localValue && !isReadOnly && (
        <p className="text-sm text-muted-foreground">
          Leave empty to auto-generate from folder path and object name
        </p>
      )}
      {localValue && (
        <p className="text-xs text-muted-foreground font-mono bg-muted px-2 py-1 rounded">
          Export reference: <span className="font-semibold">{localValue}</span>
        </p>
      )}
    </div>
  );
}
